import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PERIOD } from './config';
import { useNavigate } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ja from "date-fns/locale/ja";
import Mypage from './Mypage';
import ReservationByLocation from './ReservationByLocation';

registerLocale("ja", ja);

const year = new Date().getFullYear();
const novemberStart = new Date(year, 10, 1);
const novemberEnd = new Date(year, 10, 30);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const formatDateOnly = (date) => {
  if (!date || typeof date === 'string') return date;
  if (!(date instanceof Date) || isNaN(date)) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const HealthCheckForm = () => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();
  const navigate = useNavigate();

  // 既存モーダル用フラグ
  const [isModalOpen, setIsModalOpen] = useState(false); // Mypageモーダル
  const [isEditMode, setIsEditMode] = useState(false);
  const [isMailOptionVisible, setIsMailOptionVisible] = useState(false);
  const [isCompanyOnly, setIsCompanyOnly] = useState(false);

  // ★ 予約状況モーダル用フラグ＆選択受診場所
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [selectedLocationForReservation, setSelectedLocationForReservation] = useState(null);
  const [showReservationInfo, setShowReservationInfo] = useState(false);

  const startDate = new Date(FORM_PERIOD.start);
  const endDate = new Date(FORM_PERIOD.end);
  const now = new Date();

  const watchMailOption = watch('mailOption');

  if (now < startDate) {
    return (
      <div className="container">
        <h1>健康診断受診希望アンケート</h1>
        <p>アンケートは {startDate.toLocaleDateString()} から開始されます。</p>
      </div>
    );
  }

  if (now > endDate) {
    return (
      <div className="container">
        <h1>健康診断受診希望アンケート</h1>
        <p>アンケートの受付は終了しました。</p>
      </div>
    );
  }

  const locations = [
    "選択してください",
    "西日本産業衛生会（福岡）",
    "西日本産業衛生会（八幡）",
    "西日本産業衛生会（北九州）",
    "博多駅東クリニック"
  ];
  const gastricTests = ["選択してください", "胃カメラ", "バリウム", "受診無し"];

  const onSubmit = async (data) => {
    if (!data.firstChoice) {
      alert("第一希望日を選択してください");
      return;
    }
    if (!data.secondChoice) {
      alert("第二希望日を選択してください");
      return;
    }
    if (data.location === "博多駅東クリニック") {
      data.mailOption = "会社";
    }

    const payload = {
      ...data,
      employeeId: Number(data.employeeId),
      firstChoice: formatDateOnly(data.firstChoice),
      secondChoice: formatDateOnly(data.secondChoice),
      ...(data.mailOption === "自宅" && {
        postalCode: data.postalCode,
        address: data.address
      })
    };

    try {
      const response = await fetch(`${API_BASE_URL}/submit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("送信に失敗しました");
      const result = await response.json();
      alert(result.message);
      window.location.reload();
    } catch (error) {
      console.error("送信エラー:", error);
      alert("エラーが発生しました");
    }
  };

  const handleLocationChange = (e) => {
    const location = e.target.value;
    setValue('location', location);

    if (location === "博多駅東クリニック") {
      setIsCompanyOnly(true);
      setValue('mailOption', '会社');
    } else {
      setIsCompanyOnly(false);
    }
  };

  // ★ カレンダーが開いたら予約状況モーダルを開く
  const handleCalendarOpen = () => {
    const location = watch('location');
    if (location && location !== "選択してください") {
      setSelectedLocationForReservation(location);
      setShowReservationInfo(true); // モーダルではなくインライン表示へ
    } else {
      alert("まず受診場所を選択してください");
    }
  };

  return (
    <div className="container">
      <h1>健康診断受診希望アンケート</h1>
      <form onSubmit={handleSubmit(onSubmit)}>

        {/* 名前 */}
        <div>
          <label>名前</label>
          <input
            placeholder='例）山田太郎'
            {...register('name', { required: '名前は必須です' })}
          />
          {errors.name && <p>{errors.name.message}</p>}
        </div>

        {/* 社員番号 */}
        <div>
          <label>社員番号(1~2ケタ)</label>
          <input
            type="text"
            maxLength="2"
            placeholder='例）02 ⇒ 2'
            {...register('employeeId', {
              required: '社員番号は必須です',
              validate: (value) =>
                /^\d{1,2}$/.test(value) || '社員番号は1〜2桁の半角数字で入力してください',
            })}
            onInput={(e) => {
              const normalized = e.target.value.replace(/[０-９]/g, (s) =>
                String.fromCharCode(s.charCodeAt(0) - 65248)
              ).slice(0, 2);
              setValue('employeeId', normalized);
              e.target.value = normalized;
            }}
          />
          {errors.employeeId && <p>{errors.employeeId.message}</p>}
        </div>

        {/* 受診場所 */}
        <div>
          <label>受診場所</label>
          <select
            {...register('location', {
              required: '受診場所を選択してください',
              validate: (v) => v !== "選択してください" || '受診場所を選択してください',
            })}
            onChange={handleLocationChange}
            defaultValue="選択してください"
          >
            {locations.map((loc, i) => <option key={i} value={loc}>{loc}</option>)}
          </select>
          {errors.location && <p>{errors.location.message}</p>}
        </div>

        {/* 問診票送付先（条件付き表示） */}
        {["西日本産業衛生会（福岡）", "西日本産業衛生会（八幡）", "西日本産業衛生会（北九州）"].includes(watch('location')) && (
          <div style={{ marginTop: '10px' }}>
            <label>問診票送付先</label>
            <div style={{ display: 'flex', gap: '30px', marginTop: '5px' }}>
              <label>
                <input
                  type="radio"
                  value="自宅"
                  {...register('mailOption', { required: '郵送先を選択してください' })}
                />
                自宅
              </label>
              <label>
                <input
                  type="radio"
                  value="会社"
                  {...register('mailOption', { required: '郵送先を選択してください' })}
                />
                会社
              </label>
            </div>
            {errors.mailOption && <p>{errors.mailOption.message}</p>}

            {/* ★ 自宅が選ばれた場合のみ郵便番号・住所を表示 */}
            {watch('mailOption') === "自宅" && (
              <>
                <div style={{ marginTop: '10px' }}>
                  <label>郵便番号(ハイフン無し半角7桁)</label>
                  <input
                    type="text"
                    placeholder="例：8120011"
                    {...register('postalCode', {
                      required: '郵便番号は必須です',
                      pattern: {
                        value: /^\d{7}$/,
                        message: '郵便番号は7桁の半角数字で入力してください（ハイフンなし）',
                      }
                    })}
                  />
                  {errors.postalCode && <p>{errors.postalCode.message}</p>}
                </div>

                <div style={{ marginTop: '10px' }}>
                  <label>住所</label>
                  <input
                    type="text"
                    placeholder="例：福岡市博多区博多駅前1-1-1"
                    {...register('address', {
                      required: '住所は必須です'
                    })}
                  />
                  {errors.address && <p>{errors.address.message}</p>}
                </div>
              </>
            )}
          </div>
        )}

        {/* 第一希望日 */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <label>第一希望日</label>

          <DatePicker
            locale="ja"
            selected={watch('firstChoice') ? new Date(watch('firstChoice')) : null}
            onChange={(date) => {
              setValue('firstChoice', formatDateOnly(date));
            }}
            onCalendarOpen={() => {
              const location = watch('location');
              if (location && location !== "選択してください") {
                setSelectedLocationForReservation(location);
                setShowReservationInfo(true);
              } else {
                alert("まず受診場所を選択してください");
              }
            }}
            onCalendarClose={() => {
              setShowReservationInfo(false);
            }}
            minDate={new Date(year, 9, 1)}
            maxDate={new Date(year, 9, 31)}
            dateFormat="yyyy/MM/dd"
            placeholderText="10月から選択してください"
          />

          {/* 予約状況をカレンダーの右横に絶対配置 */}
          {showReservationInfo && selectedLocationForReservation && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '110%', // カレンダーの右隣
                width: '280px',
                maxHeight: '320px',
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: '8px',
                backgroundColor: '#fff',
                padding: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 1050,
              }}
            >
              <ReservationByLocation location={selectedLocationForReservation} />
            </div>
          )}
        </div>

        {/* 第二希望日 */}
        <div>
          <label>第二希望日</label>
          <DatePicker
            locale="ja"
            selected={watch('secondChoice') ? new Date(watch('secondChoice')) : null}
            onChange={(date) => {
              setValue('secondChoice', formatDateOnly(date));
            }}
            onCalendarOpen={() => {
              const location = watch('location');
              if (location && location !== "選択してください") {
                setSelectedLocationForReservation(location);
                setShowReservationInfo(true);
              } else {
                alert("まず受診場所を選択してください");
              }
            }}
            onCalendarClose={() => {
              setShowReservationInfo(false);
            }}
            minDate={new Date(year, 9, 1)}
            maxDate={new Date(year, 9, 31)}
            dateFormat="yyyy/MM/dd"
            placeholderText="10月から選択してください"
          />
        </div>

        {/* 胃部検査 */}
        <div>
          <label>胃部検査</label>
          <select
            {...register('gastricTest', {
              required: '胃部検査を選択してください',
              validate: (v) => v !== "選択してください" || '胃部検査を選択してください',
            })}
            defaultValue="選択してください"
          >
            {gastricTests.map((g, i) => <option key={i} value={g}>{g}</option>)}
          </select>
          {errors.gastricTest && <p>{errors.gastricTest.message}</p>}
        </div>

        {/* 任意検査希望 */}
        <div>
          <label>その他</label>
          <textarea
            placeholder="ご希望の検査、時間の要望があれば記入してください。希望しない場合は空欄でも構いません。"
            {...register('additionalTests')}
            rows="3"
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              resize: 'none'
            }}
          />
        </div>

        {/* 送信ボタン */}
        <button
          type="submit"
          style={{
            backgroundColor: isEditMode ? '#52c41a' : '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '10px 20px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          {isEditMode ? '修正' : '送信'}
        </button>

        {/* 自分のアンケート確認ボタン */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#1890ff',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            自分のアンケート確認はこちら
          </button>
        </div>
      </form>

      {/* Mypageモーダル */}
      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <Mypage
              onClose={() => setIsModalOpen(false)}
              onFound={(data) => {
                setValue('name', data.name);
                setValue('employeeId', data.employeeId.toString());
                setValue('location', data.location);
                setValue('firstChoice', data.firstChoice);
                setValue('secondChoice', data.secondChoice);
                setValue('gastricTest', data.gastricTest);
                setValue('mailOption', data.mailOption);
                setValue('postalCode', data.postalCode);
                setValue('address', data.address);
                setValue('additionalTests', data.additionalTests);
                setIsEditMode(true);
                setIsModalOpen(false);
              }}
            />
            <button onClick={() => setIsModalOpen(false)} style={styles.closeButton}>閉じる</button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0,
    width: '100vw', height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    position: 'relative',
  },
  closeButton: {
    marginTop: '15px',
    backgroundColor: '#ccc',
    border: 'none',
    borderRadius: '5px',
    padding: '8px 16px',
    cursor: 'pointer',
  }
};

export default HealthCheckForm;
